import {
  compact,
  each,
  isArray,
  isBoolean,
  isDate,
  isNumber,
  isObject,
  isString,
  map,
  omit,
  reduce,
  size,
  uniqBy,
} from "lodash";
import { Platform } from "@linkfluence/models/platform";
import { CorporaSource } from "@radarly/models/corpora";
import {
  SearchQueryAnd,
  SearchQueryBuilder,
  SearchQueryField,
} from "@radarly/models/query";
import { SocialAccount } from "@radarly/models/social_accounts";
import {
  FacebookWizardAccount,
  InstagramWizardAccount,
  SinaweiboWizardAccount,
  TwitterWizardAccount,
  WizardAccount,
  YoutubeWizardAccount,
} from "./model/account";

function getQueryTermFromWizardAccount(account: WizardAccount) {
  if (
    account.platform === "instagram" ||
    account.platform === "pinterest" ||
    account.platform === "twitter"
  ) {
    return SearchQueryAnd.getInstance(
      SearchQueryField.getPlatformField(account.platform),
      SearchQueryField.getInstance("author", `@${account.screenName}`)
    );
  }
  if (account.platform === "facebook") {
    return SearchQueryAnd.getInstance(
      SearchQueryField.getPlatformField(account.platform),
      SearchQueryField.getInstance("author", `"${account.screenName}"`)
    );
  } else if (
    account.platform === "youtube" ||
    account.platform === "sinaweibo" ||
    account.platform === "twitch"
  ) {
    return SearchQueryField.getAuthorScreenNameField(
      account.platform,
      `@${account.screenName}`
    );
  } else if (account.platform === "reddit") {
    if (account.screenName.includes("/u/")) {
      return SearchQueryField.getUserIdField(
        Platform.Reddit,
        account.screenName.replace("/u/", "")
      );
    }
    return SearchQueryField.getSubRedditField(
      account.screenName.replace("/r/", "")
    );
  } else if (account.platform === "website") {
    const reversedUrl = account.screenName.split(".").reverse().join(".");
    return SearchQueryField.getReversedUrlField(reversedUrl);
  } else if (account.platform === "tiktok") {
    return SearchQueryField.getUserIdField(
      account.platform,
      account.screenName
    );
  }
  return SearchQueryField.getUserIdField(account.platform, account.userId);
}

interface PlatformWizardAccountTransformer<T extends WizardAccount> {
  fromCorporaSource(source: CorporaSource): T;
  fromSocialAccount(account: SocialAccount): T;
}

class DefaultWizardAccountTransformer {
  fromCorporaSource(source: CorporaSource) {
    return omit(source, "created") as WizardAccount;
  }
  fromSocialAccount(account: SocialAccount) {
    return null;
  }
}

class TwitterWizardAccountTransformer
  implements PlatformWizardAccountTransformer<TwitterWizardAccount>
{
  fromCorporaSource(source: CorporaSource) {
    if (source.platform === "twitter") {
      return {
        platform: "twitter" as const,
        screenName: source.screenName,
      };
    }
  }
  fromSocialAccount(account: SocialAccount) {
    if (account.platform === "twitter") {
      return {
        platform: "twitter" as const,
        screenName: account.screenName,
      };
    }
  }
}

class FacebookWizardAccountTransformer
  implements PlatformWizardAccountTransformer<FacebookWizardAccount>
{
  fromCorporaSource(source: CorporaSource) {
    if (source.platform === "facebook") {
      return {
        platform: "facebook" as const,
        screenName: source.name,
      };
    }
  }
  fromSocialAccount(account: SocialAccount) {
    if (account.platform === "facebook") {
      return {
        platform: "facebook" as const,
        screenName: account.username,
      };
    }
  }
}

class WebsiteWizardAccountTransformer {
  fromCorporaSource(source: CorporaSource) {
    if (source.platform === "website") {
      return {
        platform: "website" as const,
        screenName: source.url || "",
      };
    }
  }
  fromSocialAccount(account: SocialAccount) {
    return account as WizardAccount;
  }
}

class InstagramWizardAccountTransformer
  implements PlatformWizardAccountTransformer<InstagramWizardAccount>
{
  fromCorporaSource(source: CorporaSource) {
    if (source.platform === "instagram") {
      return {
        platform: "instagram" as const,
        screenName: source.screenName,
      };
    }
  }
  fromSocialAccount(account: SocialAccount) {
    if (account.platform === "instagram") {
      return {
        platform: "instagram" as const,
        screenName: account.userName,
      };
    }
  }
}

class SinaweiboWizardAccountTransformer
  implements PlatformWizardAccountTransformer<SinaweiboWizardAccount>
{
  fromCorporaSource(source: CorporaSource) {
    if (source.platform === "sinaweibo") {
      return {
        platform: "sinaweibo" as const,
        screenName: source.screenName,
      };
    }
  }
  fromSocialAccount(account: SocialAccount) {
    if (account.platform === "sinaweibo") {
      return {
        platform: "sinaweibo" as const,
        screenName: account.screenName,
      };
    }
  }
}

class YoutubeWizardAccountTransformer
  implements PlatformWizardAccountTransformer<YoutubeWizardAccount>
{
  fromCorporaSource(source: CorporaSource) {
    if (source.platform === "youtube") {
      return {
        platform: "youtube" as const,
        screenName: source.name,
      };
    }
  }
  fromSocialAccount(account: SocialAccount) {
    if (account.platform === "youtube") {
      return {
        platform: "youtube" as const,
        screenName: account.name,
      };
    }
  }
}

export class WizardAccountTransformer {
  private platformTransformers: PlatformWizardAccountTransformer<WizardAccount>[] =
    [];
  public constructor(
    platformTransformers: PlatformWizardAccountTransformer<WizardAccount>[]
  ) {
    this.platformTransformers = platformTransformers;
  }
  static getInstance() {
    return new WizardAccountTransformer([
      new TwitterWizardAccountTransformer(),
      new FacebookWizardAccountTransformer(),
      new InstagramWizardAccountTransformer(),
      new SinaweiboWizardAccountTransformer(),
      new YoutubeWizardAccountTransformer(),
      new WebsiteWizardAccountTransformer(),
      new DefaultWizardAccountTransformer(),
    ]);
  }
  public fromCorporaSource(source: CorporaSource) {
    let socialAccount: WizardAccount = null;
    each(this.platformTransformers, (transformer) => {
      if (!socialAccount) {
        const platformSocialAccount = transformer.fromCorporaSource(source);
        if (platformSocialAccount) {
          socialAccount = platformSocialAccount;
        }
      }
    });
    return socialAccount;
  }
  public fromCorporaSources(sources: CorporaSource[]) {
    return getDedupedAccounts(
      compact(map(sources, (source) => this.fromCorporaSource(source)))
    );
  }
  public fromSocialAccount(account: SocialAccount) {
    let socialAccount: WizardAccount = null;
    each(this.platformTransformers, (transformer) => {
      if (!socialAccount) {
        const platformSocialAccount = transformer.fromSocialAccount(account);
        if (platformSocialAccount) {
          socialAccount = platformSocialAccount;
        }
      }
    });
    return socialAccount;
  }
  public fromSocialAccounts(accounts: SocialAccount[]) {
    return getDedupedAccounts(
      compact(map(accounts, (account) => this.fromSocialAccount(account)))
    );
  }
}

export function getClauseFromWizardAccount(account: WizardAccount) {
  const builder = SearchQueryBuilder.getInstance();
  builder.add(getQueryTermFromWizardAccount(account));
  return {
    and: [
      {
        query: builder.generateQuery(),
      },
    ],
  };
}

const reduceEmptyValues = (values) => {
  return reduce(
    values,
    (acc, value, key: string) => {
      if (
        isString(value) ||
        isBoolean(value) ||
        isNumber(value) ||
        isDate(value) ||
        ((isObject(value) || isArray(value)) && size(value))
      ) {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );
};

function orderObject<T extends object>(unordered: T) {
  const ordered = {};
  Object.keys(reduceEmptyValues(unordered))
    .sort()
    .forEach((key) => {
      const value = unordered[key];
      if (
        isString(value) ||
        isBoolean(value) ||
        isNumber(value) ||
        isArray(value) ||
        isDate(value)
      ) {
        ordered[key] = value;
      } else if (isObject(value)) {
        ordered[key] = orderObject(value);
      }
    });
  return ordered as T;
}

function getQueryFromWizardAccounts(accounts: WizardAccount[]) {
  const builder = SearchQueryBuilder.getInstance();
  each(getDedupedAccounts(accounts), (account) => {
    builder.add(getQueryTermFromWizardAccount(account));
  });
  return builder.generateQuery();
}

export function getClauseFromWizardAccounts(
  accounts: WizardAccount[],
  without: boolean = false
) {
  const clause = {
    query: getQueryFromWizardAccounts(accounts),
  };
  return {
    and: [without ? { not: clause } : clause],
  };
}

export function getDedupedAccounts(accounts: WizardAccount[]) {
  return uniqBy(accounts, (account) => JSON.stringify(orderObject(account)));
}
